import "./post.css";
import { Link } from "react-router-dom";
import React, { useEffect, useState, useMemo } from "react";
import { decodeHtml } from "../../utils/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleRight } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { toast } from "react-toastify";

export default function Post({ post, categoriesFromDB }) {
  const [author, setAuthor] = useState({ profilePic: "", avatar_urls: [] });

  const { wordCount, readingTime } = useMemo(() => {
    const getWordCount = (htmlString) => {
      if (!htmlString) return 0;

      // 1. Remove image elements
      let cleaned = htmlString.replace(/<img[^>]*>/gi, " ");
      cleaned = cleaned.replace(/<div[^>]*class="ql-customImage"[^>]*>[\s\S]*?<\/div>/gi, " ");

      // 2. Decode HTML entities
      const textarea = document.createElement("textarea");
      textarea.innerHTML = cleaned;
      let decoded = textarea.value;

      // 3. Strip all remaining HTML tags
      decoded = decoded.replace(/<[^>]*>/g, " ");

      // 4. Normalize punctuation and spacing
      decoded = decoded
        .replace(/[\u2014\u2013—–]/g, "—")
        .replace(/\u00A0/g, " ")
        .replace(/\u2026|\.\.\./g, "...")
        .replace(/[“”‘’]/g, "'")
        .replace(/["]/g, '"')
        .replace(/’/g, "'")
        .replace(/(\S)(\.\.\.)/g, '$1 ...')
        .replace(/(\.\.\.)(\S)/g, '... $2')
        .replace(/(\S)([—])/g, '$1 —')
        .replace(/([—])(\S)/g, '— $2')
        .replace(/\s+/g, " ")
        .trim();

      // 5. Tokenize by space
      const tokens = decoded.split(" ");
      const dropped = [];

      // 6. Unicode-aware word validation
      const words = tokens.filter(word => {
        const cleaned = word.replace(/^[^\p{L}\p{N}'"-]+|[^\p{L}\p{N}'"-]+$/gu, '');
        const isValid = /[\p{L}\p{N}]/u.test(cleaned);
        if (!isValid) dropped.push(word);
        return isValid;
      });

      return words.length;
    };

    const wordCount = getWordCount(post.desc);
    const readingTime = Math.max(1, Math.ceil(wordCount / 200)); // avg reading speed assuming 200 wpm
    return { wordCount, readingTime };
  }, [post.desc]);

  const activeCategoryMap = useMemo(() => {
    if (!Array.isArray(categoriesFromDB)) return {};
    return categoriesFromDB
      .filter((cat) => cat.isActive)
      .reduce((map, cat) => {
        map[cat.cat_id] = cat;
        return map;
      }, {});
  }, [categoriesFromDB]);

  useEffect(() => {
    const getAuthor = async () => {
      try {
        const res = await axios.get("/users/" + post.authorId);

        if (res.status !== 200) {
          toast.info(res.data.message);
        }

        // if (res.data.avatar_urls["96"] !== undefined) {
        //   res.data.profilePic = res.data.avatar_urls["96"];
        // }

        setAuthor(res.data);
      } catch (err) {
        console.error("Error fetching author" + err);
        toast.error("Error Fetching Writer!");
      }
    };

    getAuthor();
  }, [post.authorId]); // authorId in the dependency array, ensuring authors refresh when new page posts come in.

  return (
    <div className="post">
      <Link to={`/${post.post_slug}`} className="link">
        {post.photo && (
          <img
            className="postImg"
            src={
              post.photo.startsWith("http")
                ? post.photo
                : process.env.REACT_APP_AWS_S3_IMAGE_URL + post.photo
            }
            alt=""
          />
        )}
      </Link>
      <div className="postInfo">
        <div className="postCats">
          {post.categories
            ?.filter(
              (c) =>
                c.cat_id &&
                Array.isArray(categoriesFromDB) &&
                categoriesFromDB.some(
                  (dbCat) => dbCat.cat_id === c.cat_id && dbCat.isActive
                )
            )
            .map((c) => (
              <Link
                to={`/category/${c.cat_slug}`}
                key={c._id ?? c.cat_id ?? c.cat_name}
                className="postCat link"
              >
                {decodeHtml(c.cat_name)}
              </Link>
            ))
          }
        </div>
        <Link to={`/${post.post_slug}`} className="link">
          <div className="postTitleContainer">
            <span className="postTitle">{decodeHtml(post.title)}</span>
          </div>
        </Link>
        <hr />
        <div className="postDetails">
          <Link to={`/author/${author.userId}`} className="link">
            <img
              className="authorImg"
              src={
                author && author.avatar_urls?.["48"]
                  ? process.env.REACT_APP_AWS_S3_IMAGE_URL + author.avatar_urls["48"]
                  : author.profilePic
              }
              alt={`${author.user_name} Img`}
            />
          </Link>
          <Link to={`/author/${author.userId}`} className="link">
            <span className="authorName-Post">
              {decodeHtml(author.user_name)}
            </span>
          </Link>
          <span className="postDate">
            {new Date(post.publishedAt).toDateString()}
          </span>
        </div>
      </div>
      <p
        style={{ marginBottom: "15px" }}
        className="postDesc"
        dangerouslySetInnerHTML={{ __html: post.shortDesc }}
      ></p>
      <div className="postDetails">
        <span className="postDate" style={{ display: "block", textAlign: "left", width: "100%", marginLeft: "15px", marginBottom: "10px" }}>
          {readingTime} min read · {wordCount} words
        </span>
      </div>
      <Link to={`/${post.post_slug}`} className="readMoreButton">
        Read More{" "}
        <FontAwesomeIcon icon={faAngleRight} style={{ marginLeft: "20px" }} />
      </Link>
    </div>
  );
}
